import { FC, useState } from 'react';
import { Box, Group, IconCaretDown } from '@screentone/core';

import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { PageDto, ParametersInput, useGetSectionParameterSchemasQuery } from 'data/generated/graphql';
import SectionParameterField from '../artifact-parameters/components/SectionParameterField';
import styles from '../artifact-settings.module.scss';

interface ClientSectionParametersProps {
  isReadOnly?: boolean;
}

const ClientSectionParameters: FC<ClientSectionParametersProps> = ({ isReadOnly = false }) => {
  const { getDTO, getSectionAttributes, setSectionAttributes } = useDataModelContext();
  const [expanded, setExpanded] = useState(false);

  const dto = getDTO(true) as PageDto;
  const {
    metadata: { publicationKey }
  } = dto;

  const { data } = useGetSectionParameterSchemasQuery({ publicationKey }, { enabled: !!publicationKey });
  const clientSchemas = (data?.getSectionParameterSchemas ?? []).filter((s) => s.userExposed === true);

  const sectionAttributes = getSectionAttributes();

  const getSectionParamValue = (key: string): string =>
    sectionAttributes.sectionParameters?.find((p) => p.name === key)?.value ?? '';

  const handleSectionParamChange = (key: string, value: string) => {
    const existing = sectionAttributes.sectionParameters ?? [];
    const updated: ParametersInput[] = existing.some((p) => p.name === key)
      ? existing.map((p) => (p.name === key ? { ...p, value } : p))
      : [...existing, { name: key, value, type: 'parameter' }];
    setSectionAttributes({ ...sectionAttributes, sectionParameters: updated });
  };

  if (!clientSchemas.length) {
    return null;
  }

  return (
    <Box>
      <Box.Title onClick={() => setExpanded((prev) => !prev)} className={styles.collapsibleWrapper} variant="header3">
        Client Section Parameters <IconCaretDown />
      </Box.Title>
      <Box.Content style={{ display: expanded ? 'block' : 'none' }}>
        <Group gap="md" direction="column">
          {clientSchemas.map((schema) => (
            <SectionParameterField
              key={schema.key}
              schema={schema}
              currentValue={getSectionParamValue(schema.key)}
              isReadOnly={isReadOnly}
              onChange={handleSectionParamChange}
            />
          ))}
        </Group>
      </Box.Content>
    </Box>
  );
};

export default ClientSectionParameters;
